/**
 * The builder's words, once (`docs/builder-engine-and-consistency.md` §4.2).
 *
 * Every string on the boards that the quote builder, the package builder and
 * the catalog's service pop-out share is here and nowhere else — a screen
 * reads it, it never types it. Extend this file; never reword what is in it,
 * because each value is the literal on a board (`docs/build-exactly-as-drawn.md`
 * law 3), curly apostrophes included.
 */

export const OPTIONAL_LABEL = 'Optional'
export const OPTIONAL_SUB = 'The customer decides whether to add it'
/*
 * A package visit in some plans only is an answer, and an answer cannot also
 * be an extra (step 24 item 1) — the package builder's twin of the quote's
 * refusal, which the server words (`CHOICE_MADE_OPTIONAL`).
 */
export const PLAN_VISIT_OPTIONAL =
  'This visit is in some of the plans, so it can\u2019t also be optional. Tick every plan for it first.'
export const SHOW_ON_PROPOSAL_LABEL = 'Show on proposal'
export const TAXABLE_LABEL = 'Taxable'
/** The sub-text under Taxable on a SERVICE only. */
export const TAXABLE_SERVICE_SUB = 'every line it adds is taxed'
export const CONSUMES_STOCK_LABEL = 'Consumes stock'
/** The package setting — the one place that is not the flag. */
export const STARTS_AS_OPTIONAL_LABEL = 'Starts as optional'
export const OPTIONS_SECTION = 'Options'
export const REMOVE = 'Remove'
export const DONE = 'Done'
export const RESET = 'Reset'
export const PROPOSAL_TIMING_LABELS = ['Same as quote', 'Count', 'Frequency', 'Season', 'Dates'] as const
export const MEASURED_BY_LABEL = 'Measured by'
export const MEASURED_BY_HINT = 'the quote fills it in from the property'
export const MEASURED_BY_NONE = 'No measure'
export const MEASURED_BY_NONE_SUB = 'the estimator types a quantity on each line'
/** Phone sheet: '…table is made of.' */
export const MEASURED_BY_MENU_FOOT = 'Your measurement types — the same list a property’s Measurements table uses.'
export const QTY_BASE_LABEL = 'Base'
export const QTY_BASE_HINT = 'whatever the size'
export const QTY_RATE_HINT = 'what grows with the measure'
export const TIERS_TITLE = 'Tiers by size'
export const TIERS_SUB = 'A different rate as the job gets bigger.'
export const TIERS_ADD = 'Add tiers'
export const TIERS_REMOVE = 'Remove tiers'
/** The unit is the measurement type’s (§6.7 item 2) — “sq ft” on the board. */
export const TIERS_EXPLAIN = (unit: string) =>
  `Each tier’s rate is charged only on the ${unit} inside it, so a bigger job never costs less than a smaller one. The base is charged once.`
export const VISIT_MENU = {
  rename: 'Rename', duplicate: 'Duplicate', addAfter: 'Add a visit after this one',
  optional: OPTIONAL_LABEL, optionalSub: OPTIONAL_SUB,
  moveUp: 'Move up', moveDown: 'Move down', collapse: 'Collapse', remove: 'Remove',
} as const

// ---------------------------------------------------------------------------
// Board 7's popover chrome — the words around the fields
// ---------------------------------------------------------------------------

/** The proposal popover's title (Board 7, "What the customer reads"). */
export const CUSTOMER_READS_TITLE = 'What the customer reads'
/** Its footer while nothing on the visit overrides the quote. */
export const FOLLOWING_THE_QUOTE = 'Following the quote.'
/** The footer total sentences, `<label> $amount` (Board 7 footers). */
export const VISIT_TOTAL = 'Visit total'
export const SERVICE_TOTAL = 'Service total'
export const LINE_TOTAL = 'Line total'
/** The package visit's footer, which prices at the sample measure. */
export const AT_SAMPLE_MEASURE = 'At the sample measure'

// ---------------------------------------------------------------------------
// The line grid's header and its tips (folder 44 §5.1–5.3, board qb-02 B)
// ---------------------------------------------------------------------------

/** Header labels, left to right. Qty spans the quantity and the unit. */
export const COLUMN_LABELS = {
  qty: 'Qty', priceEach: 'Price each', price: 'Price', cost: 'Cost', margin: 'Margin',
  /** A package's quantity cell: a base and a rate the measure resolves (cb-05 callout 4). */
  qtyRule: 'Qty rule',
} as const
/** One sentence per header. `{target}` is the company's margin target. */
export const COLUMN_TIPS = {
  qty: 'How many, in this line’s own unit. Type to override; a kit’s other lines follow unless you edited them.',
  priceEach: 'What the customer pays for one unit. When you changed it, the standard price is struck through underneath.',
  price: 'Qty × Price each. This is the line the customer sees and the number that adds up to the quote total.',
  cost: 'What the whole line costs us — quantity × our cost each. Never printed, never in Customer view.',
  margin: '(Price − Cost) ÷ Price. Amber when it is under this company’s target, {target}%.',
} as const
/** What Price each folds into under the name when the card is narrow (§5.5). */
export const PRICE_EACH_FOLDED = (rate: string) => `× ${rate} each`

// ---------------------------------------------------------------------------
// The margin panel, the visit chip and the rail (folder 44 §6–§7, qb-03/04/07/09)
// ---------------------------------------------------------------------------

export const MARGIN_TITLE = 'Margin'
export const INTERNAL_CHIP = 'Internal'
export const CUSTOMER_SEES_CHIP = 'Customer sees this'
export const SCOPE_LABELS = { base: 'Base', options: 'Options', all: 'All' } as const
/** The helper under the scope control; the plan wording when the quote has a question. */
export const SCOPE_HELP = {
  base: 'Base: what they pay if they add nothing.',
  options: 'Options only: optional visits, services and lines.',
  all: 'All: base plus every option taken.',
} as const
export const SCOPE_HELP_PLANS = {
  options: (upper: string, floor: string) => `Options: what ${upper} add to ${floor}.`,
  all: 'All: the top choice, with every option taken.',
} as const
export const NOTHING_OPTIONAL = 'Nothing is optional on this quote, so Options and All would repeat Base.'
export const PTS_ABOVE = (pts: string) => `${pts} pts above target`
export const PTS_BELOW = (pts: string) => `${pts} pts below target`
export const GROSS_ON = 'gross on'
export const TARGET_TICK = (pct: number) => `target ${pct}%`
export const COST_OF_PRICE = { before: 'Cost', after: 'of price' } as const
export const MARGIN_OF = 'Margin'
export const TABLE_HEAD = { sold: 'Sold', cost: 'Cost', margin: 'Margin', total: 'Total' } as const
export const BREAKDOWN_LABELS = { visit: 'By visit', division: 'By division', choice: 'By choice' } as const
export const MISSING_COST = (n: number) =>
  `${n} ${n === 1 ? 'line has' : 'lines have'} no cost, so this margin is overstated.`
export const FIX_THEM = 'Fix them ›'
export const NOT_FINAL = 'Not final'
export const NOT_COUNTED = 'Not counted:'
export const OPEN_ENDED_REASON = '— open-ended, bills while on rent.'
export const SUBS_UNDER_LABOR = 'Subs are counted under Labor.'
export const NO_PRICED_LINES = 'No priced lines yet.'
export const NET_CHANGE_NOTE = 'Some costs go down in the upper choices.'
/** The visit popover and sheet (qb-04, qb-09 · 2). */
export const VISIT_SCOPE_LABELS = { base: 'Base only', withOptions: 'With options' } as const
export const OPTIONAL_VISIT_NOTE = 'Optional visit — none of this is in the base price. It is added only if the customer takes it.'
export const LOWEST_LINES = 'Lowest margin lines'
export const WHOLE_QUOTE = 'Whole quote'
/** The phone margin sheet's sub-line (qb-09 · 2). */
export const MARGIN_FACTS = (count: string, hours: string) => `Margin · ${count} · ${hours}`
export const OPEN_MARGIN = 'Open Margin ›'
export const CHIP_MARGIN = 'margin'
/** The rail (qb-07). */
export const QUOTE_TOTAL_TITLE = 'Quote total'
export const BASE_PRICE = 'Base price'
/** The package page's rail (cb-05, §7.1). */
export const PRICE_AT_SAMPLE = 'Price at the sample'
export const BASE_PRICE_AT = (sample: string, unit: string) => `Base price · ${sample} ${unit}`
export const WORKED_OUT_AT = (sample: string, unit: string) =>
  `Worked out at ${sample} ${unit} by the server’s preview — the same maths a quote uses.`
export const BILLING_IN_PACKAGE = 'Billing in this package'
export const ON_VISITS = (names: string) => `On ${names}`
export const ADD_BILLING_PLAN = '+ Add a billing plan'
export const TRY_IT_WITH = 'Try it with'
export const SAVE_CHANGES = 'Save changes'
export const DUPLICATE = 'Duplicate'
export const OFFERED = 'Offered'
export const PACKAGE_TAG = 'Package'
export const AT_THE_SAMPLE_PLAN = (plan: string | null) => (plan ? `at the sample · ${plan}` : 'at the sample')
export const SETTINGS_LINK = 'Settings ›'
export const BASE_PRICE_SUB = 'if they take nothing extra'
export const OPTIONAL_NOT_IN_BASE = 'Optional · not in base'
export const IF_ALL_OPTIONS = 'If all options taken'
export const BEFORE_YOU_SEND = 'Before you send'
export const TO_CHECK = (n: number) => `${n} to check`
/** Board 11's chip when nothing needs you. */
export const ALL_CLEAR = 'All clear'
export const REVIEW = 'Review ›'
/** An ordering check's link (visits §5.4, Board 10). */
export const FIX = 'Fix ›'
export const QUOTE_SUMMARY = 'Quote summary'
/** The totals bar under the builder on an iPad and a phone (qb-08 A, qb-09). */
export const WITH_OPTIONS = (amount: string) => `${amount} with options`
export const SUMMARY = 'Summary'
export const PREVIEW = 'Preview'
/*
 * The bar's fixed send words are gone (folder 52 §1, §4):
 * the green button is `jobActions()`'s primary, in `job-stages/copy.ts`'s
 * words — *Send to Karen*, *Approve*, *Schedule* …
 */
export const BAR_BASE_MARGIN = (pct: string) => `base · ${pct} margin`

// ---------------------------------------------------------------------------
// Choices (quote-build-refresh §8–§9, boards qb-05, qb-06, qb-10)
// ---------------------------------------------------------------------------

export const CHOICES_LABEL = 'Choices'
export const CHOICES_NONE = 'Not part of a choice'
export const CHOICES_HINT = 'Group visits into choices the customer picks between — like Silver · Gold · Platinum. A visit can be in several.'
export const CHOICES_FIND = 'Find or create a choice…'
export const CHOICES_CREATE_WORD = 'Create'
export const CHOICES_CREATE = (name: string) => `${CHOICES_CREATE_WORD} “${name}”`
export const CHOICES_CREATE_IN = (name: string, question: string) => `Create “${name}” in ${question}`
export const CHOICES_FIRST = 'first choice'
export const CHOICES_EMPTY = 'No choices on this quote yet. The first one also starts the question the customer is asked — “Your choice” — which you can reword once it exists.'
export const CHOICES_NEW = 'New choice'
export const CHOICES_NEW_QUESTION = 'New question'
export const CHOICES_ADD = '+ Add'
export const CHOICES_VISITS = (n: number) => `${n} visit${n === 1 ? '' : 's'}`
export const CHOICES_OPTIONAL_REASON = 'An optional add-on can’t also be part of a choice — a choice already makes it optional. Turn the add-on off to use Choices.'
export const IN_A_CHOICE = 'In a choice already.'
export const CUSTOMER_IS_ASKED = 'The customer is asked'
export const REWORD = 'Reword'
export const SAVE = 'Save'
export const REWORD_HINT = (names: string) => `This is the heading on the proposal above ${names}. Shared by every visit in these choices.`
export const EVERY_PRICE = 'This visit will be in every price.'
export const MOVE_IT = 'Move it'
export const KEEP_IT_IN = (question: string) => `Keep it in ${question}`
export const ALREADY_TICKED = 'Already ticked'
/** "Silver, Gold and Platinum". */
export function andList(names: string[]): string {
  if (names.length <= 1) return names[0] ?? ''
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
}
const WORDS = ['no', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten']
/** qb-05 · 4: "In 3 choices. This visit’s price counts once in each — five visits appear in …". */
export function inChoicesText(choices: string[], trips: number | null, long: boolean): string {
  const head = `In ${choices.length} choice${choices.length === 1 ? '' : 's'}.`
  if (choices.length < 2) return head
  const lead = `${head} This visit’s price counts once in each`
  if (!long || trips === null) return `${lead}.`
  const count = trips <= 10 ? WORDS[trips]! : String(trips)
  return `${lead} — ${count} visit${trips === 1 ? '' : 's'} appear${trips === 1 ? 's' : ''} in ${andList(choices)}.`
}

/** The plans card (qb-06). */
export const PICKS_ONE = (floor: string) => `The customer picks one · the cheapest, ${floor}, is in the base price`
export const ADD_CHOICE = 'Add choice'
/* The choice box (Aaron, step 21 item 4). */
export const ADD_CHOICES = 'Add choices'
export const ADD_VISITS = 'Add visits'
export const NEW_VISIT_IN_CHOICES = 'New visit'
export const NO_LOOSE_VISITS = 'Every visit is already in a choice box'
export const CHOICE_BOX_EMPTY = 'Add the choices the customer picks between, then the visits in each'
export const CHOICE_SETTINGS = 'Choice box settings'
export const CHOICE_SETTINGS_FACTS = 'Choice box · the customer picks one'
export const OPTION_SETTINGS_FACTS = (visits: number) => `Choice · ${visits} visit${visits === 1 ? '' : 's'}`
export const CHOOSE_NONE = 'Customer may choose none'
export const CHOOSE_NONE_SUB = 'Every choice becomes an option on top of the base price.'
export const RECOMMENDED = 'Recommended'
export const RECOMMENDED_SUB = 'Marked on the proposal. One choice per box.'
export const REMOVE_CHOICE = 'Remove choice'
export const REMOVE_CHOICE_BOX = 'Remove choice box'
/** Step 22 item 2.1: **Add choice** is the choice's own popup, empty, with Add choice as its action. */
export const NEW_CHOICE = 'New choice'
export const NEW_CHOICE_FACTS = (box: string) => `A new answer to ${box}`
/** Step 22 item 2.2: a visit in the box and in no choice — not on the proposal until one is ticked. */
export const NOT_ON_PROPOSAL = 'Not on proposal'
export const NOT_ON_PROPOSAL_WHY = 'In no choice yet, so the customer does not see it. Tick a choice to put it on the proposal.'
export const IN_BOX_NO_CHOICE = (box: string) =>
  `In ${box}, in no choice yet — it is not on the proposal until one is ticked.`
/** Step 22 item 2.3: the ⋮ on a choice box's row and on its header. */
export const REMOVE_FROM_BOX = 'Remove from choice box'
export const GO_TO_VISIT = 'Go to visit'
export const CHOICE_BOX_RENAME = 'Rename…'
/** Step 22 item 2.4: + Add visits, in the related-dropdown style. */
export const FIND_A_VISIT = 'Find a visit…'
/* The Billing tab's toolbar and plan popup (Aaron, step 21 item 5). */
export const NEW_BILLING_PLAN = 'New billing plan'
export const SEARCH_BILLING_PLANS = 'Search billing plans…'
export const NO_PLANS_MATCH = (text: string) => `No billing plan or visit matches “${text}”.`
export const PLAN_SETTINGS_FACTS = (kind: string, visits: number) => `Billing plan · ${kind} · ${visits} visit${visits === 1 ? '' : 's'}`
export const REMOVE_PLAN = 'Remove plan'
export const DROP_FOR_NEW_PLAN = 'Drop a visit here to give it a billing plan of its own'
export const INVOICE_SCOPE_LABELS = { base: 'Base only', all: 'With options' } as const
export const VISITS_IN_THE_PLAN = 'Visits in the plan'
export const PLAN_ROW_VISITS = 'Visits'
export const PLAN_ROW_PRICE = 'Price'
export const PLAN_ROW_COST = 'Cost'
export const PLAN_ROW_MARGIN = 'Margin'

/** The rail with choices (qb-06 right, §9.3). */
export const PICKS_ONE_SECTION = (question: string) => `${question} · customer picks one`
export const IN_BASE = 'in base'
export const LOWEST_CHOICE = (floor: string) => `${floor}, the lowest choice`
export const IF_TOP_CHOICE = 'If top choice taken'
export const UPGRADE_ADDS = 'Each upgrade adds:'
export const UPGRADES_EARN_MORE = 'The upgrades earn more than the base.'
export const EARNS_LESS = (name: string) => `${name} earns less than the base.`

// ---------------------------------------------------------------------------
// The service pop-out (cb-04, builder-engine §6) and the quantity rule (cb-19, §6.8)
// ---------------------------------------------------------------------------

export const SERVICE_SUB = (division: string | null) => (division ? `Service · ${division}` : 'Service')
export const WHAT_IT_IS = 'What it is'
export const MEASURED_BY_HINT_PHONE = 'a measurement type — the quote fills it in from the property'
export const MEASURE_TYPES_FOOT_PHONE = 'Your measurement types — the same list a property’s Measurements table is made of.'
export const MEASURE_SHEET_SUB = (service: string) => `${service} · pick a measurement type`
export const USE_TYPE = (type: string) => `Use ${type}`
export const MINIMUM_CHARGE = 'Minimum charge'
export const MINIMUM_CHARGE_HINT = 'what a small job still costs'
export const STATUS = 'Status'
export const ACTIVE = 'Active'
export const WHATS_IN_IT = 'What’s in it'
export const WHATS_IN_IT_EMPTY_SUB = 'optional — most services are just a name and a measure'
export const WHATS_IN_IT_SUB = (unit: string | null) => (unit ? `optional · base + rate per ${unit}` : 'optional')
export const NOTHING_ADDED = 'Nothing added, and that is fine. The estimator adds lines on each quote. '
  + 'Add them here only when the service always brings the same materials, labor, equipment or fees.'
export const TRY_IT_AT = 'Try it at'
export const AT_MEASURE = (measure: string, unit: string) => `At ${measure} ${unit}`
export const WHERE_OFFERED = 'Where it is offered'
export const WHERE_OFFERED_HINT = 'every branch, including any added later'
export const CANCEL = 'Cancel'
export const PER_UNIT = (unit: string) => `per ${unit}`
export const RESOLVES_TO = (qty: string, unit: string, measure: string, measureUnit: string) =>
  `= ${qty} ${unit} at ${measure} ${measureUnit}`
export const WHATEVER_THE_SIZE = '= whatever the size'
/** Under a package's Qty rule: "= 1.2 bag at 6,000" (cb-05 callout 4). */
export const RULE_RESOLVES = (qty: string, unit: string, sample: string) => `= ${qty} ${unit} at ${sample}`
/** After the rate box: "/ sq ft". */
export const PER_MEASURE = (unit: string) => `/ ${unit}`
export const TIERS_CHIP = (count: number) => `${count} tiers`
export const LINE_COST_CHIP = (cost: string) => `cost ${cost}`

/** The Line popover's How much (cb-19 cells 2–3). */
export const HOW_MUCH = 'How much'
export const HOW_MUCH_SUB = 'scaled by the service’s measure'
export const PER_OF = (unit: string, type: string) => `Per ${unit} of ${type}`
export const AT_THE_SAMPLE = 'at the sample.'
export const TIER_FROM = 'From'
export const TIER_UP_TO = 'Up to'
export const TIER_PER = (unit: string) => `Per ${unit}`
export const AND_UP = 'and up'
export const ADD_TIER = '+ Add tier'
export const OUR_COST_PER = (unit: string) => `Our cost / ${unit}`
export const STANDARD_PER = (unit: string) => `Standard / ${unit}`
export const QUOTED_PER = (unit: string) => `Quoted / ${unit}`
export const MARKUP = 'Markup'

/** The recipe line popover's facts line (cb-19 · 2): "Labor · a line of Mulch beds". */
export const RECIPE_LINE_FACTS = (kind: string, service: string) => `${kind} · a line of ${service}`
export const NEW_SERVICE = 'New service'
export const NAME_LABEL = 'Name'
export const DIVISION_LABEL = 'Division'
export const PICK_A_DIVISION = 'Pick a division'
export const NAME_NEEDED = 'A service needs a name'
export const DIVISION_NEEDED = 'Which division is this service?'
/** The locked totals strip under a recipe (cb-04 · 2). */
export const STRIP_PRICE = 'price'
export const STRIP_COST = 'cost'
export const STRIP_MARGIN = 'margin'

/** The quote's Measure popover (cb-19 · 1, builder-engine §6.7 item 4). */
export const MEASURED_BY_TYPE = (type: string) => `Measured by ${type}`
export const PROPERTYS_MEASURE = (type: string) => `The property’s ${type}`
export const FOLLOWS_THE_PROPERTY = 'it follows the property if it is measured again'
export const MEASURE_SOURCE: Record<string, string> = { walked: 'Walked', aerial: 'Aerial', typed: 'Typed' }
export const QUOTE_ONLY = 'A number for this quote only'
export const PROPERTY_KEEPS = (measure: string) => `The property keeps its ${measure}.`
export const LINES_RESCALE = 'Lines re-scale as you choose.'
export const NO_MEASURE_YET = (type: string) => `This property has no ${type} yet.`
export const NO_MEASURE_YET_SUB = 'Type it here, or measure it on the property’s Map tab. Until then the lines sit at their bases.'
export const MEASURE_PLACEHOLDER = 'e.g. 1,420'
export const SAVE_ON_PROPERTY = 'Save it on the property too'
export const SAVE_ON_PROPERTY_SUB = 'It is added to the property’s Measurements as typed, today, by you.'
export const NOTHING_UNTIL_DONE = 'Nothing is added until you press Done.'
export const MEASURE_CHIP_EMPTY = (type: string) => `${type} —`

// ---------------------------------------------------------------------------
// A visit's When (visits contract §4.5, Board 8) — every word literal
// ---------------------------------------------------------------------------

export const WHEN_SECTION = 'When'
export const WHEN_SECTION_SUB = 'starts · repeats · ends · how long'
export const WHEN_ROW = { starts: 'Starts', repeats: 'Repeats', ends: 'Ends', length: 'Length' } as const
export const KIND_OF_VISIT = 'Kind of visit'
export const AUTO_NAMED = 'auto-named'
export const READS = 'Reads:'
export const ON_THE_PROPOSAL = 'On the proposal:'
export const ADD_DATE = '+ Add date'
export const DAY_ON_SITE = (n: number) => (n === 1 ? 'day on site' : 'days on site')
export const VISITS_WORD = 'visits'
/** One visit is a visit — "After 1 visits" was audit B's cb-08 defect. */
export const visitsWordFor = (n: number): string => (n === 1 ? 'visit' : VISITS_WORD)
export const LAST_ON = (day: string) => `last: ${day}`
export const OFF_IN_SETTINGS = 'Off in Settings'
export const WEEKDAY_BUTTONS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'] as const

/** One menu row: the name, and the one line of help Board 8 draws under it. */
export interface MenuWords { label: string; help: string }

export const VISIT_KIND_MENU: Record<'work' | 'inspect' | 'place' | 'service' | 'swap' | 'pickup', MenuWords> = {
  work: { label: 'Work', help: 'The crew does the job' },
  inspect: { label: 'Inspect', help: 'Look, measure or diagnose — no work done' },
  place: { label: 'Drop off', help: 'Deliver something that stays' },
  service: { label: 'Service', help: 'A repeat visit to look after it' },
  swap: { label: 'Swap', help: 'Take one away, leave a fresh one' },
  pickup: { label: 'Pick up', help: 'Take it back' },
}

export const VISIT_STARTS_MENU: Record<'date' | 'after' | 'term_end' | 'on_call', MenuWords> = {
  date: { label: 'On a date', help: 'Pick the day, or leave it blank and schedule later' },
  after: { label: 'After another visit', help: 'Follows a visit on this quote' },
  term_end: { label: 'When the rental ends', help: 'Follows the end of the rental term' },
  on_call: { label: 'When called', help: 'Scheduled when the customer asks' },
}

/** Board 8 cell 9: in a package, *At the start* takes *On a date*'s row — a package has no dates. */
export const PACKAGE_STARTS_AT: MenuWords = {
  label: 'At the start', help: 'Dated when the package goes on a quote',
}

export const VISIT_REPEATS_MENU: Record<'none' | 'every' | 'dates', MenuWords> = {
  none: { label: 'Doesn’t repeat', help: 'One visit' },
  every: { label: 'Every', help: 'Every N days, weeks, months or years' },
  dates: { label: 'On specific dates', help: 'Pick each date yourself' },
}

export const VISIT_ENDS_MENU: Record<'date' | 'count' | 'term_end' | 'never', MenuWords> = {
  date: { label: 'On a date', help: 'Stops at the last visit on or before the date' },
  count: { label: 'After', help: 'Stops after a number of visits' },
  term_end: { label: 'When the rental ends', help: 'Follows the end of the rental term' },
  never: { label: 'Never — until cancelled', help: 'Keeps going until you cancel it' },
}

export const VISIT_UNIT_MENU: Record<'days' | 'business_days' | 'weeks', MenuWords> = {
  days: { label: 'days', help: 'Calendar days, weekends included' },
  business_days: { label: 'business days', help: 'Monday to Friday' },
  weeks: { label: 'weeks', help: 'Seven calendar days each' },
}

export const VISIT_EVENT_MENU: Record<'done' | 'starts', MenuWords> = {
  done: { label: 'after it is done', help: 'Counts from the day that visit is finished' },
  starts: { label: 'after it starts', help: 'Counts from the day that visit begins — 0 days is the same day' },
}

/** The repeat unit, singular and plural (`week` · `weeks`). */
export const REPEAT_UNIT_WORDS = {
  day: ['day', 'days'], week: ['week', 'weeks'], month: ['month', 'months'], year: ['year', 'years'],
} as const

/** The lag unit read beside its number: `1 business day` · `7 days` · `2 weeks` (Board 8). */
export const LAG_UNIT_WORDS = {
  days: ['day', 'days'], business_days: ['business day', 'business days'], weeks: ['week', 'weeks'],
} as const

/** Board 17: on a phone the unlabelled rows of Board 8 carry a label above them. */
export const WHEN_PHONE_ROW = {
  anchor: 'Which visit', lag: 'How long after', firstOn: 'First visit', lastOn: 'Last visit',
} as const
/** Board 17 cell 3: the Starts list sheet's one line. */
export const STARTS_SHEET_SUB = 'when this visit begins'

/** Settings › Product › Visit kinds (visits §4.5.1, Board 21) — every word literal. */
export const VISIT_KINDS_PAGE = {
  title: 'Visit kinds',
  heading: 'Which kinds of visit does your business use?',
  sub: 'Turn off the ones you never schedule. They leave the Kind menu, so nobody picks one by mistake; visits already using one keep it.',
  /** Board 21 cell 3 — the phone's shorter line. */
  phoneSub: 'Turn off the kinds you never schedule. Visits already using one keep it.',
  startFrom: 'Start from',
  presets: {
    landscaping: 'Landscaping & lawn care',
    repair: 'Repair trades',
    roofing: 'Roofing & building',
    rentals: 'Rentals',
    all: 'All six',
  },
  /** Split around the bold word: `A starting point only. Change any switch and it becomes **Custom**.` */
  presetNote: ['A starting point only. Change any switch and it becomes ', 'Custom', '.'],
  alwaysOn: 'Always on',
  visits: (n: number) => `${n.toLocaleString('en-US')} visit${n === 1 ? '' : 's'}`,
  notUsed: 'not used yet',
  inUseOff: (n: number) =>
    `${n} visit${n === 1 ? '' : 's'} on open quotes use${n === 1 ? 's' : ''} it. They keep it; it just cannot be picked for a new visit.`,
  footer: 'Saved for the whole company. Packages keep working; a kind that is off shows “Off in Settings” on a package’s visit.',
  cancel: 'Cancel',
  save: 'Save',
} as const

/** The Add visit menu (visits §5.1.1, Board 10; phone Board 17 cell 1) — every word literal. */
export const ADD_VISIT_MENU = {
  blank: { label: 'Blank visit', help: 'Name it, then add services to it' },
  service: { label: 'From a service…', help: 'Pick a service — its visit is made for you' },
  package: { label: 'From a package…', help: 'Visits arrive ready-made: Drop off, Service, Pick up' },
  copy: { label: 'Copy of a visit…', help: 'Start from a visit already on this quote' },
  other: { label: 'Copy from another quote…', help: 'Bring visits over from a past quote' },
} as const
export const ADD_VISIT_SHEET = { title: 'Add a visit', sub: 'five ways to start' } as const

/*
 * Moving a visit that others follow (visits §5.3, Board 10; phone Board 17
 * cell 5). One function inflects the numbers: 1 → `One visit follows it:` /
 * `Move both`; 2 → `Two visits follow it:` / `Move all three`; spelled out to
 * nine, digits from ten (`Move all 11`).
 */
const NUMBER_WORDS = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine']
const numberWord = (n: number): string => (n < 10 ? NUMBER_WORDS[n]! : String(n))
const capital = (word: string): string => word.charAt(0).toUpperCase() + word.slice(1)
export const MOVE_PROMPT = {
  moving: 'Moving',
  to: 'to',
  /** `Two visits follow it:` */
  follow: (n: number) => `${capital(numberWord(n))} visit${n === 1 ? '' : 's'} follow${n === 1 ? 's' : ''} it:`,
  /** `Move all three` — the visit and its followers. */
  moveAll: (followers: number) => (followers === 1 ? 'Move both' : `Move all ${numberWord(followers + 1)}`),
  onlyThis: 'Only this one',
  escape: 'Esc to cancel',
  cancel: 'Cancel',
  sheetTitle: 'Move a visit others follow?',
  sheetSub: 'a prompt, not a surprise',
} as const

/** cb-07: the visit popover's Billing section. */
export const BILLING_SECTION = 'Billing'
export const BILLING_PLAN_HINT = 'invoiced the way this plan says'
export const OPEN_BILLING_TAB = 'Open the Billing tab ›'

/** cb-18 · 5: a phone service with nothing in it — one sentence in the dashed box, no sub-line. */
export const NOTHING_ADDED_PHONE = 'Optional — most services are a name and a measure.'

/** cb-22 · 4: a phone tier is a card — `Tier 2` · `from 3,001 sq ft` · Remove. */
export const TIER_CARD = (n: number) => `Tier ${n}`
export const TIER_CARD_FROM = (from: string, unit: string) => `from ${from} ${unit}`

/* The phone's service screen (qb-10 · 4): the measure card's label and the bar's sub-line. */
export const MEASURE_DRIVES_LINES = 'Measure · drives the lines'
export const BAR_VISIT_MARGIN = (pct: string) => `base · this visit · ${pct} margin`

/** cb-07: the visit popover's Notes section — one box, three audiences. */
export const NOTES_SECTION = 'Notes'
export type NoteAudience = 'quote' | 'internal' | 'crew'
export const NOTE_TABS: Record<NoteAudience, string> = { quote: 'Customer', internal: 'Internal', crew: 'Crew' }
/** Under the box: cb-07 for Customer, qb-05 for Crew. No board draws one for Internal. */
export const NOTE_HINTS: Record<NoteAudience, string | null> = {
  quote: 'Prints on the proposal under this visit.',
  internal: null,
  crew: 'Shows on the iPad for every visit this becomes.',
}
/** The box's accessible name per audience — the three labels it had as three boxes. */
export const NOTE_ARIA: Record<NoteAudience, string> = {
  quote: 'Note on the proposal', internal: 'Internal note', crew: 'Crew note',
}

/** cb-04 · 3: the + Materials picker's last row, `Create “pav” as a material`, and a row's unit. */
export const CREATE_AS_MATERIAL = { create: 'Create', asA: 'as a material' } as const
export const PICK_PER_UNIT = (unit: string) => `per ${unit}`

/*
 * Aaron's item 3: the service picker's recipe chip (3.3) — how many lines a
 * catalog service's own recipe holds, `Empty` where it holds none — and the
 * picker Add visit ▾ → From a service… opens (3.2), whose pick makes a visit.
 */
export const PICK_RECIPE_ITEMS = (count: number) =>
  (count === 0 ? 'Empty' : `${count} ${count === 1 ? 'item' : 'items'}`)
export const PICK_SERVICE_AS_VISIT = {
  label: 'Add a visit from a service',
  footer: '↑↓ move · ⏎ add it as a new visit',
} as const

/*
 * Aaron's item 5.1: **New billing plan ▾** is Add visit ▾'s menu over the five
 * plan kinds — on a phone the same rows in a sheet, named like Add a visit's.
 */
export const NEW_PLAN_SHEET = { title: NEW_BILLING_PLAN, sub: 'five ways to bill' } as const

/*
 * Aaron's review item 3: a service's notes — the visit's Customer · Internal ·
 * Crew block, on a catalog service and on a quote's service (migration 0125),
 * and on a package's service (0127), which wears the quote's hints.
 * Only the hints differ; the tabs and the box's names are `NOTE_TABS` and
 * `NOTE_ARIA`. Customer is `services.description` in the catalog, the
 * package's own words where it has some, and the quote service's own copy on
 * a quote — which is what the proposal prints.
 */
export const SERVICE_NOTE_HINTS: Record<NoteAudience, string | null> = {
  quote: 'Prints on the proposal under this service.',
  internal: null,
  crew: 'For the crew. Never on the proposal.',
}
/*
 * Aaron, step 25: a package's service note that has been changed can be put
 * back to the catalog's, which is the only way it goes on *following* the
 * catalog — clearing the box means "no note on this package".
 */
export const USE_CATALOG_WORDS = 'Use the catalog\u2019s words'
export const CATALOG_SERVICE_NOTE_HINTS: Record<NoteAudience, string | null> = {
  quote: 'Prints on the proposal under this service. Each quote it is added to gets its own copy.',
  internal: 'Each quote it is added to gets its own copy. Never on the proposal.',
  crew: 'Each quote it is added to gets its own copy. Never on the proposal.',
}

/*
 * Aaron's review item 4: the ⋮ on a service and on a line is the visit's menu
 * one level down — Rename · Duplicate · Add … below · ✓ Optional · Move up ·
 * Move down · Collapse · Remove, in that order, with `VISIT_MENU`'s words
 * wherever they apply. The one that cannot is *Add a visit after this one*:
 * a service adds a service under itself, and a line adds an item — Aaron's
 * word for a line of any kind, the one the service picker's recipe chip
 * counts in (`PICK_RECIPE_ITEMS`). Remove keeps the words each row's menu
 * already had, and a line's Reset sits where a service's Collapse does.
 */
export const ROW_MENU = {
  addServiceBelow: 'Add a service below',
  addItemBelow: 'Add an item below',
  /** A folded visit's or service's Collapse, said the other way. */
  expand: 'Expand',
  resetPrice: 'Reset to standard price',
  removeService: 'Remove service',
  removeLine: 'Remove line',
} as const

/*
 * **Customer-facing name** (Aaron, step 24; migration 0126) — at the top of
 * the line popover, on a quote and on a recipe alike, with the catalog forms'
 * label (`components/forms/catalog-copy.ts`). The placeholder is the name a
 * blank box prints, so an empty box reads as "the same as the name"; the hint
 * says how far a rename reaches, which is the one thing that differs.
 */
export const CUSTOMER_NAME_LABEL = 'Customer-facing name'
export const CUSTOMER_NAME_QUOTE_HINT = 'What the proposal prints, on this quote only. Blank uses the name.'
export const CUSTOMER_NAME_RECIPE_HINT = 'What the proposal prints. Blank uses the catalog’s.'
